import React, { useState, useEffect, useMemo } from "react";
import { Plus } from "lucide-react";
import { getDataByAll } from "../../../api/venueApi"; 

// Import Komponen Modular
import SectionHeader from "../../Common/SectionHeader";
import SectionFilter from "../../Common/SectionFilter";
import SectionTable from "../../Common/SectionTable";
import Pagination from "../../Common/SectionTablePagination";

import IndexRow from "./IndexRow";
import Form from "./Form";

const VenueIndex = () => {
  const [datas, setDatas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [sortColumn, setSortColumn] = useState("None");
  const itemsPerPage = 5;

  // STATE UNTUK NAVIGASI HALAMAN
  const [viewMode, setViewMode] = useState("list"); 
  const [selectedVenue, setSelectedVenue] = useState(null);

  // MENGGUNAKAN POLA YANG SAMA DENGAN WORKSHOP
  useEffect(() => {
    setLoading(true);
    getDataByAll()
      .then((data) => setDatas(data))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, sortColumn]);

  const handlePageChange = (pageNumber) => {
    setCurrentPage(pageNumber);
  };

  // HANDLER NAVIGASI
  const handleAddNew = () => {
    setSelectedVenue(null);
    setViewMode("form");
  };

  const handleEdit = (venue) => {
    setSelectedVenue(venue);
    setViewMode("form");
  };

  const handleBack = () => {
    setViewMode("list");
  };

  const handleSaveVenue = (data) => {
    console.log("Saving Venue Data:", data);
    // Integrasikan dengan API .NET Anda di sini
    setViewMode("list");
  };

  // LOGIKA PENGURUTAN DAN PENYARINGAN
  const processedVenues = useMemo(() => {
    return [...datas]
      .filter((v) =>
        v.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (v.location && v.location.toLowerCase().includes(searchTerm.toLowerCase()))
      )
      .sort((a, b) => {
        if (sortColumn === "None") return 0;
        const keyMap = {
          "Venue Name": "name",
          "Location": "location",
          "Capacity": "capacity",
          "Status": "status"
        };
        const key = keyMap[sortColumn];
        if (!key) return 0;
        const valA = (a[key] || "").toString().toLowerCase();
        const valB = (b[key] || "").toString().toLowerCase();
        
        // Sorting angka untuk Capacity agar akurat
        if (key === "capacity") return a[key] - b[key];

        return valA < valB ? -1 : 1;
      });
  }, [datas, searchTerm, sortColumn]);

  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = processedVenues.slice(indexOfFirstItem, indexOfLastItem);

  const tableHeaders = [
    { label: "Venue Name" },
    { label: "Location" },
    { label: "Capacity", className: "text-center" },
    { label: "Status", className: "text-center" },
    { label: "Actions", className: "text-center" },
  ];

  // RENDER HALAMAN FORM
  if (viewMode === "form") {
    return (
      <Form 
        onBack={handleBack} 
        onSave={handleSaveVenue}
        initialData={selectedVenue}
      />
    );
  }

  return (
    <div className="p-6 space-y-6 bg-white dark:bg-slate-900 rounded-[2rem] shadow-sm border border-stone-100 dark:border-white/5">
      <SectionHeader 
        title="Venue Management"
        subtitle="Manage event locations, spaces and capacities"
        actionLabel="Create Venue"
        Icon={Plus}
        onActionClick={handleAddNew}
      />

      <SectionFilter 
        searchTerm={searchTerm}
        onSearchChange={setSearchTerm}
        placeholder="Search by venue name or location..."
        columns={tableHeaders}
        selectedColumn={sortColumn}
        onColumnSelect={setSortColumn}
      />

      <SectionTable 
        headers={tableHeaders}
        data={currentItems}
        loading={loading}
        renderRow={(venue) => (
          <IndexRow 
            key={venue.id}
            venue={venue} 
            onEdit={handleEdit} 
          />
        )}
        emptyMessage={`No venues found for "${searchTerm}"`}
      />

      <div className="pt-6 border-t border-stone-100 dark:border-white/5">
        <Pagination 
          totalItems={processedVenues.length}
          itemsPerPage={itemsPerPage}
          currentPage={currentPage}
          onPageChange={handlePageChange}
        />
      </div>
    </div>
  );
};

export default VenueIndex;